import { useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { clearAccountIDB } from "../db/fsDB";

/* =====================================================
   ⚙️ SETTINGS SOCKET HOOK
===================================================== */

export function useSettingsSocket({
  socket,
  myChatId,

  // state updaters (optional but recommended)
  setCurrentUser,
  setUsers,
  setSelectedItem,
    handleLogout,
      onRentRefundRequired,
}) {
  /* =====================================================
     🔤 UPDATE USERNAME
  ===================================================== */
  const updateUsername = useCallback(
    (newUsername) => {
      if (!socket || !myChatId) return;

      // frontend sends raw value (no @)
      socket.emit("user-update-username", {
        chatId: myChatId,
        newUsername,
      });
    },
    [socket, myChatId]
  );

  /* =====================================================
     🔐 UPDATE ACCOUNT (EMAIL / PASSWORD)
     - supports confirmPassword
  ===================================================== */
  const updateAccount = useCallback(
  ({ currentPassword, newEmail, newPassword, confirmPassword }) => {
    if (!socket || !currentPassword || !myChatId) return;

    socket.emit("user-update-account", {
      chatId: myChatId, // ✅ CORRECT
      currentPassword,
      newEmail,
      newPassword,
      confirmPassword,
    });
  },
  [socket, myChatId]
);

/* =====================================================
   🧾 UPDATE DISPLAY NAME (NO @, CASE-ONLY)
===================================================== */
const updateDisplayName = useCallback(
  (displayName) => {
    if (!socket || !myChatId) return;

    // ensure frontend never sends "@"
    const clean = displayName.replace(/^@+/, "");

    socket.emit("user-update-display-name", {
      chatId: myChatId,
      displayName: clean,
    });
  },
  [socket, myChatId]
);

/* =====================================================
   🗑️ DELETE ACCOUNT (FULL WIPE)
===================================================== */
const deleteAccount = useCallback(
  ({ currentPassword }) => {
    if (!socket || !myChatId || !currentPassword) return;

    socket.emit("user-delete-account", {
      chatId: myChatId,
      currentPassword,
    });
  },
  [socket, myChatId]
);

  /* =====================================================
     🖼️ FETCH AVATAR (FROM DB)
  ===================================================== */
  const fetchAvatar = useCallback(() => {
    if (!socket || !myChatId) return;

    socket.emit("user-get-avatar", {
      chatId: myChatId,
    });
  }, [socket, myChatId]);

  /* =====================================================
     🖼️ UPDATE AVATAR
  ===================================================== */
  const updateAvatar = useCallback(
    (imageUrl) => {
      if (!socket || !myChatId || !imageUrl) return;

      socket.emit("user-update-avatar", {
        chatId: myChatId,
        imageUrl,
      });
    },
    [socket, myChatId]
  );

  /* =====================================================
     📥 SOCKET LISTENERS
  ===================================================== */
  useEffect(() => {
    if (!socket) return;

    /* ---------- USERNAME SUCCESS ---------- */
    const onUsernameSuccess = ({ chatId, username }) => {
      if (String(chatId) !== String(myChatId)) return;

      toast.success("Username updated");

      setCurrentUser?.((prev) =>
        prev ? { ...prev, username } : prev
      );

      setUsers?.((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(chatId)
            ? { ...u, username }
            : u
        )
      );

      setSelectedItem?.((prev) => {
        if (
          prev?.type === "user" &&
          String(prev.chatId) === String(chatId)
        ) {
          return { ...prev, username };
        }
        return prev;
      });
    };

    /* ---------- USERNAME FAILURE ---------- */
    const onUsernameFailed = ({ reason }) => {
      const map = {
        missing_fields: "Username is required",
        invalid_length: "Username must be between 4 and 15 characters",
        invalid_characters:
          "Only letters, numbers, and underscores are allowed",
        must_start_with_letter: "Username must start with a letter",
        looks_like_phone_number:
          "Username cannot look like a phone number",
        impersonation_username:
          "This username looks like an official or admin account",
        reserved_username:
          "This username is reserved and cannot be used",
        username_taken: "This username is already taken",
        same_username: "This is already your username",
        user_not_found: "User not found",
        server_error: "Server error. Please try again",
      };

      toast.error(map[reason] || "Failed to update username");
    };

    /* ---------- ACCOUNT UPDATE SUCCESS ---------- */
    const onAccountUpdateSuccess = ({
      email,
      passwordUpdated,
    }) => {
      if (email) {
        setCurrentUser?.((prev) =>
          prev ? { ...prev, email } : prev
        );
      }

      toast.success(
        passwordUpdated
          ? "Password updated successfully"
          : "Account updated successfully"
      );
    };

    const onDeleteAccountRentRefundRequired = (payload) => {

    onRentRefundRequired?.(payload);

};

    /* ---------- ACCOUNT UPDATE FAILURE ---------- */
    const onAccountUpdateFailed = ({ reason }) => {
      const map = {
        missing_fields: "Required fields are missing",
        nothing_to_update: "Nothing to update",
        user_not_found: "User not found",
        invalid_password: "Current password is incorrect",

        confirm_password_required:
          "Please confirm your new password",
        password_mismatch: "Passwords do not match",
        weak_password:
          "Password must contain letters, numbers & symbols (min 8 chars)",

        invalid_email: "Invalid email address",
        same_email: "This is already your email",
        email_taken: "Email is already in use",

        server_error: "Server error. Please try again",
      };

      toast.error(map[reason] || "Failed to update account");
    };

    /* ---------- USERNAME LIVE UPDATE ---------- */
    const onUsernameUpdated = ({ chatId, username }) => {
      setUsers?.((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(chatId)
            ? { ...u, username }
            : u
        )
      );

      setSelectedItem?.((prev) => {
        if (
          prev?.type === "user" &&
          String(prev.chatId) === String(chatId)
        ) {
          return { ...prev, username };
        }
        return prev;
      });
    };

    /* ---------- ACCOUNT DELETE SUCCESS ---------- */
const onAccountDeleteSuccess = async () => {

  await clearAccountIDB();

  toast.success("Account deleted successfully");

  await handleLogout?.();

};

/* ---------- ACCOUNT DELETE FAILURE ---------- */
const onAccountDeleteFailed = ({ reason }) => {
  const map = {
    missing_fields: "Password is required",
    user_not_found: "User not found",
    invalid_password: "Current password is incorrect",
    funds_committed:
      "You still have locked or escrowed funds. Resolve them first.",
    server_error: "Server error. Please try again",
  };

  toast.error(map[reason] || "Failed to delete account");
};

/* ---------- DISPLAY NAME LIVE UPDATE ---------- */
const onDisplayNameUpdated = ({ chatId, displayName }) => {
  setUsers?.((prev) =>
    prev.map((u) =>
      String(u.chatId) === String(chatId)
        ? { ...u, displayName }
        : u
    )
  );

  setSelectedItem?.((prev) => {
    if (
      prev?.type === "user" &&
      String(prev.chatId) === String(chatId)
    ) {
      return { ...prev, displayName };
    }
    return prev;
  });
};

/* ---------- DISPLAY NAME SUCCESS ---------- */
const onDisplayNameSuccess = ({ chatId, displayName }) => {
  if (String(chatId) !== String(myChatId)) return;

  toast.success("Display name updated");

  setCurrentUser?.((prev) =>
    prev ? { ...prev, displayName } : prev
  );

  setUsers?.((prev) =>
    prev.map((u) =>
      String(u.chatId) === String(chatId)
        ? { ...u, displayName }
        : u
    )
  );

  setSelectedItem?.((prev) => {
    if (
      prev?.type === "user" &&
      String(prev.chatId) === String(chatId)
    ) {
      return { ...prev, displayName };
    }
    return prev;
  });
};

/* ---------- DISPLAY NAME FAILURE ---------- */
const onDisplayNameFailed = ({ reason }) => {
  const map = {
    missing_fields: "Display name is required",
    display_name_must_match_username:
      "Display name must match your username (capitalization only)",
    display_name_taken: "This display name is already taken",
    user_not_found: "User not found",
    server_error: "Server error. Please try again",
  };

  toast.error(map[reason] || "Failed to update display name");
};


    /* ---------- AVATAR FETCH ---------- */
    const onAvatarFetched = ({
      chatId,
      profileImageUrl,
    }) => {
      if (String(chatId) !== String(myChatId)) return;

      setCurrentUser?.((prev) =>
        prev ? { ...prev, profileImageUrl } : prev
      );
    };

    /* ---------- AVATAR LIVE UPDATE ---------- */
    const onAvatarUpdated = ({
      chatId,
      profileImageUrl,
    }) => {
      setUsers?.((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(chatId)
            ? { ...u, profileImageUrl }
            : u
        )
      );

      setSelectedItem?.((prev) => {
        if (
          prev?.type === "user" &&
          String(prev.chatId) === String(chatId)
        ) {
          return { ...prev, profileImageUrl };
        }
        return prev;
      });

      if (String(chatId) === String(myChatId)) {
        setCurrentUser?.((prev) =>
          prev ? { ...prev, profileImageUrl } : prev
        );
      }
    };

    /* ---------- REGISTER ---------- */
    socket.on("user-update-username-success", onUsernameSuccess);
    socket.on("user-update-username-failed", onUsernameFailed);
    socket.on("user-username-updated", onUsernameUpdated);

    socket.on("user-update-account-success", onAccountUpdateSuccess);
    socket.on("user-update-account-failed", onAccountUpdateFailed);

    socket.on("user-avatar", onAvatarFetched);
    socket.on("user-avatar-updated", onAvatarUpdated);

    socket.on("user-delete-account-success", onAccountDeleteSuccess);
    socket.on(
    "user-delete-account-rent-refund-required",
    onDeleteAccountRentRefundRequired
);
socket.on("user-delete-account-failed", onAccountDeleteFailed);

socket.on("user-update-display-name-success", onDisplayNameSuccess);
socket.on("user-update-display-name-failed", onDisplayNameFailed);
socket.on("user-display-name-updated", onDisplayNameUpdated);



    /* ---------- CLEANUP ---------- */
    return () => {
      socket.off("user-update-username-success", onUsernameSuccess);
      socket.off("user-update-username-failed", onUsernameFailed);
      socket.off("user-username-updated", onUsernameUpdated);

      socket.off("user-update-account-success", onAccountUpdateSuccess);
      socket.off("user-update-account-failed", onAccountUpdateFailed);

      socket.off("user-avatar", onAvatarFetched);
      socket.off("user-avatar-updated", onAvatarUpdated);

      socket.off("user-delete-account-success", onAccountDeleteSuccess);
      socket.off(
    "user-delete-account-rent-refund-required",
    onDeleteAccountRentRefundRequired
);
socket.off("user-delete-account-failed", onAccountDeleteFailed);

socket.off("user-update-display-name-success", onDisplayNameSuccess);
socket.off("user-update-display-name-failed", onDisplayNameFailed);
socket.off("user-display-name-updated", onDisplayNameUpdated);


    };
  }, [
    socket,
    myChatId,
    setCurrentUser,
    setUsers,
    setSelectedItem,
      onRentRefundRequired,
  ]);

  /* =====================================================
     🚀 AUTO-FETCH AVATAR ON CONNECT
  ===================================================== */
  useEffect(() => {
    fetchAvatar();
  }, [fetchAvatar]);

  return {
    updateUsername,
    updateAvatar,
    fetchAvatar,
    updateAccount,
     deleteAccount,
       updateDisplayName,
  };
}
